/**
 * @module NotificationsShared
 * @package @geenius/notifications/shared
 * @description Exposes the shared notification contract, configuration
 * primitives, and presentation helpers consumed by every package variant.
 * This is the root public API for the framework-agnostic notifications layer.
 */


/** Re-exports shared configuration helpers for notifications consumers. */
export {
  configureNotifications,
  defineNotificationsConfig,
  getNotificationsConfig,
  isNotificationsConfigured,
  mergeNotificationsConfig,
  NotificationsConfigError,
  resetNotificationsConfig,
} from './config'
/** Re-exports shared FSL helpers for localized labels, motion-friendly metadata,
 * and page SEO. */
export {
  buildNotificationsSeoMeta,
  formatNotificationDateLabel,
  formatNotificationRelativeTime,
  getNotificationChannelLabel,
  getNotificationStatusLabel,
  getNotificationTypeLabel,
  groupNotificationsForDisplay,
  NOTIFICATION_CHANNEL_TRANSLATION_KEYS,
  NOTIFICATION_DEFAULT_TRANSLATIONS,
  NOTIFICATION_STATUS_TRANSLATION_KEYS,
  NOTIFICATION_TYPE_TRANSLATION_KEYS,
  pluralizeNotifications,
  translateNotifications,
} from './fsl'
/** Re-exports the shared notification type contract. */
export type {
  Notification,
  NotificationBatch,
  NotificationChannel,
  NotificationConfig,
  NotificationMetadata,
  NotificationPreferences,
  NotificationProviderConfig,
  NotificationQuietHours,
  NotificationRealtimeTransport,
  NotificationStatus,
  NotificationType,
} from './types'
/** Re-exports shared notification enum-like constants. */
export {
  NOTIFICATION_CHANNELS,
  NOTIFICATION_STATUSES,
  NOTIFICATION_TYPES,
} from './types'

import type {
  Notification,
  NotificationChannel,
  NotificationType,
} from './types'

/**
 * Shared UI metadata for notification badges and inbox treatments.
 *
 * @property label Human-readable label used in filters and badges.
 * @property color OKLCH color token used for accents and backgrounds.
 * @property icon Short visual marker used in list items and chips.
 */
export interface NotificationTypeDescriptor {
  label: string
  color: string
  icon: string
}

/**
 * Map of notification type metadata used by every UI variant.
 */
export const TYPE_CONFIG: Record<NotificationType, NotificationTypeDescriptor> = {
  info: { label: 'Info', color: 'oklch(0.65 0.20 265)', icon: 'ℹ️' },
  success: { label: 'Success', color: 'oklch(0.72 0.18 155)', icon: '✅' },
  warning: { label: 'Warning', color: 'oklch(0.72 0.18 60)', icon: '⚠️' },
  error: { label: 'Error', color: 'oklch(0.60 0.25 25)', icon: '❌' },
  mention: { label: 'Mention', color: 'oklch(0.65 0.22 290)', icon: '@' },
  invite: { label: 'Invite', color: 'oklch(0.68 0.18 200)', icon: '✉️' },
  payment: { label: 'Payment', color: 'oklch(0.72 0.20 145)', icon: '💰' },
  system: { label: 'System', color: 'oklch(0.55 0.10 250)', icon: '⚙️' },
}

/**
 * Human-readable labels for supported notification channels.
 */
export const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  'in-app': 'In-App',
  email: 'Email',
  push: 'Push',
  sms: 'SMS',
}

/**
 * Default per-channel preference state used when preferences do not yet exist.
 */
export const DEFAULT_CHANNEL_PREFS: Record<NotificationChannel, boolean> = {
  'in-app': true,
  email: true,
  push: false,
  sms: false,
}

/**
 * Default per-type preference state used for new users.
 */
export const DEFAULT_TYPE_PREFS: Record<NotificationType, boolean> = {
  info: true,
  success: true,
  warning: true,
  error: true,
  mention: true,
  invite: true,
  payment: true,
  system: true,
}

function isNotificationType(type: string): type is NotificationType {
  return Object.hasOwn(TYPE_CONFIG, type)
}

function getNotificationDescriptor(
  type: string,
): NotificationTypeDescriptor | undefined {
  return isNotificationType(type) ? TYPE_CONFIG[type] : undefined
}

/**
 * Returns the configured icon for a notification type key.
 *
 * @param type Notification type or arbitrary string key.
 * @returns The icon configured for the type, or the default info icon.
 */
export function getNotificationIcon(type: string): string {
  return getNotificationDescriptor(type)?.icon ?? 'ℹ️'
}

/**
 * Returns the configured accent color for a notification type key.
 *
 * @param type Notification type or arbitrary string key.
 * @returns The OKLCH color configured for the type, or a neutral fallback.
 */
export function getNotificationColor(type: string): string {
  return getNotificationDescriptor(type)?.color ?? 'oklch(0.55 0.10 250)'
}

/**
 * Formats a notification timestamp into a compact relative label.
 *
 * @param createdAt ISO timestamp associated with the notification.
 * @returns A compact relative time string such as `5m ago`.
 */
export function formatNotificationTime(createdAt: string): string {
  const timestamp = new Date(createdAt).getTime()
  const diffMs = Date.now() - timestamp
  const minutes = Math.floor(diffMs / 60_000)

  if (minutes < 1) {
    return 'just now'
  }

  if (minutes < 60) {
    return `${minutes}m ago`
  }

  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    return `${hours}h ago`
  }

  const days = Math.floor(hours / 24)
  if (days < 7) {
    return `${days}d ago`
  }

  if (days < 30) {
    return `${Math.floor(days / 7)}w ago`
  }

  return new Date(createdAt).toLocaleDateString()
}

/**
 * Groups notifications into date buckets suitable for inbox sections.
 *
 * @param notifications Notification records to group.
 * @returns A map keyed by display labels such as `Today` and `Yesterday`.
 *
 * @example
 * ```ts
 * const grouped = groupByDate(notifications)
 * const todayItems = grouped.get('Today') ?? []
 * ```
 */
export function groupByDate(
  notifications: Notification[],
): Map<string, Notification[]> {
  const groups = new Map<string, Notification[]>()
  const now = new Date()
  const today = now.toDateString()
  const yesterday = new Date(now.getTime() - 86_400_000).toDateString()

  for (const notification of notifications) {
    const notificationDate = new Date(notification.createdAt)
    const label =
      notificationDate.toDateString() === today
        ? 'Today'
        : notificationDate.toDateString() === yesterday
          ? 'Yesterday'
          : notificationDate.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            })

    const bucket = groups.get(label)
    if (bucket) {
      bucket.push(notification)
    } else {
      groups.set(label, [notification])
    }
  }

  return groups
}

/**
 * Returns a new array with every unread notification marked as read.
 *
 * @param notifications Notification records to update.
 * @returns A new array with unread notifications marked as read.
 */
export function markAllRead(notifications: Notification[]): Notification[] {
  const readAt = new Date().toISOString()

  return notifications.map((notification) =>
    notification.status === 'unread'
      ? { ...notification, status: 'read', readAt }
      : notification,
  )
}
